import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Smartphone, KeyRound, Copy } from "lucide-react";

export default function TwoFactorPanel({ onEnrolled, mandatory: mandatoryProp }) {
  const [status, setStatus] = useState(null);
  const [enrollData, setEnrollData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/auth/2fa/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, []);

  const mandatory = mandatoryProp ?? status?.mandatory;

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll");
      setEnrollData(data);
      setBackupCodes(null);
      setCode("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const verifyEnroll = async () => {
    if (code.length !== 6) { toast.error("Masukkan 6 digit kode"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code });
      setBackupCodes(data.backup_codes);
      setEnrollData(null);
      toast.success("2FA aktif!");
      await loadStatus();
      onEnrolled?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kode salah"); }
    finally { setBusy(false); }
  };

  const disable2fa = async () => {
    if (disableCode.length !== 6) { toast.error("Masukkan 6 digit kode"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      toast.success("2FA dinonaktifkan");
      setDisableCode("");
      await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((backupCodes || []).join("\n"));
    toast.success("Backup codes disalin");
  };

  if (!status) return null;

  // Backup codes display (after successful enrollment)
  if (backupCodes) {
    return (
      <Card data-testid="twofa-backup-codes">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2"><KeyRound className="h-5 w-5" /> Simpan Backup Codes</CardTitle>
          <CardDescription>Simpan kode ini di tempat aman. Tiap kode hanya bisa dipakai sekali jika Anda kehilangan perangkat authenticator.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-secondary/50 p-4 font-mono text-sm">
            {backupCodes.map((c) => <div key={c} data-testid="backup-code">{c}</div>)}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyCodes} data-testid="copy-backup-codes"><Copy className="mr-1.5 h-4 w-4" /> Salin</Button>
            <Button size="sm" onClick={() => setBackupCodes(null)} data-testid="backup-codes-done">Selesai</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Enrollment in progress (QR shown)
  if (enrollData) {
    return (
      <Card data-testid="twofa-enroll">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2"><Smartphone className="h-5 w-5" /> Scan dengan Authenticator</CardTitle>
          <CardDescription>Scan QR ini dengan Google Authenticator / Authy, lalu masukkan 6 digit kode untuk mengaktifkan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <img src={`data:image/png;base64,${enrollData.qr_image_base64}`} alt="QR 2FA" className="mx-auto h-44 w-44 rounded-lg border border-border" data-testid="twofa-qr" />
          <p className="text-center text-xs text-muted-foreground break-all">Kunci manual: <span className="font-mono">{enrollData.secret}</span></p>
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="twofa-otp-input">
              <InputOTPGroup>
                <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button className="w-full" onClick={verifyEnroll} disabled={busy} data-testid="twofa-verify-btn">
            {busy ? "Memverifikasi..." : "Verifikasi & Aktifkan"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Status view
  return (
    <Card data-testid="twofa-panel">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> Autentikasi Dua Faktor (2FA)
          {status.enabled
            ? <Badge className="bg-emerald-100 text-emerald-800" data-testid="twofa-status-on">Aktif</Badge>
            : <Badge className="bg-amber-100 text-amber-800" data-testid="twofa-status-off">Nonaktif</Badge>}
        </CardTitle>
        <CardDescription>
          {mandatory
            ? "2FA WAJIB untuk role Anda (owner/super admin) demi keamanan akun & data clinic."
            : "Tambahkan lapisan keamanan ekstra menggunakan aplikasi authenticator."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.enabled ? (
          <>
            <p className="text-sm text-muted-foreground">Backup codes tersisa: <span className="font-medium text-foreground">{status.backup_codes_remaining}</span></p>
            {!mandatory && (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium"><ShieldAlert className="h-4 w-4 text-rose-600" /> Nonaktifkan 2FA</div>
                <div className="flex justify-start">
                  <InputOTP maxLength={6} value={disableCode} onChange={setDisableCode} data-testid="twofa-disable-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button variant="destructive" size="sm" onClick={disable2fa} disabled={busy} data-testid="twofa-disable-btn">Nonaktifkan</Button>
              </div>
            )}
          </>
        ) : (
          <Button onClick={startEnroll} disabled={busy} data-testid="twofa-enable-btn">
            {busy ? "Memuat..." : "Aktifkan 2FA"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
